<template>
    <div
        class="wrapper modal-address-clarification"
        :class="{
            'form--active': formActive,
        }"
    >
        <div class="form">
            <div class="placemarks-head">
                <div class="placemarks-head__title">Новый адрес доставки</div>
            </div>
            <div class="form-wrapper">
                <div class="address-input">
                    <AutocompleteInput
                        placeholder="Введите название улицы"
                        label="Улица, номер дома"
                        :default-value="address.value"
                        :suggestions="address.suggestions"
                        :process="processAutocomplete"
                        :classes="[
                            'form-control',
                            !address.valid && address.msg ? 'is-invalid' : '',
                            address.valid ? 'is-valid' : '',
                        ]"
                        @handle-lazy-keyup="fetchSuggestionsAddress"
                        @handle-set-suggestion="selectSuggestionAddress"
                        @handle-change="(val) => (address.value = val)"
                        @handle-focus="() => (focusState = true)"
                        @handle-blur="() => (focusState = false)"
                    />
                </div>

                <template v-if="isFastDelivery">
                    <div class="status-area">
                        <div
                            v-if="processCheckingAddress"
                            class="status-block status-block--checking"
                        >
                            <div class="status-icon">
                                <span class="spinner"></span>
                            </div>
                            <div class="status-text">
                                Проверяем возможность доставки...
                            </div>
                        </div>
                        <div
                            v-else-if="
                                availableDeliveryStatus !==
                                DeliveryStatus.UNKNOWN
                            "
                            :class="[
                                'status-block',
                                availableDeliveryStatus ===
                                    DeliveryStatus.AVAILABLE &&
                                !!props.defaultStore
                                    ? 'status-block--success'
                                    : 'status-block--warning',
                            ]"
                        >
                            <div class="status-icon">
                                <span :class="availableDeliveryIcon"></span>
                            </div>
                            <div class="status-text">
                                {{ availableDeliveryText }}
                            </div>
                        </div>
                        <div v-else class="status-block status-block--info">
                            <div class="status-icon">
                                <span class="info"></span>
                            </div>
                            <div class="status-text">
                                {{ validationMessage }}
                            </div>
                        </div>
                    </div>
                </template>
                <template v-else>
                    <div
                        v-if="house && !canProceed"
                        class="status-block status-block--info"
                    >
                        <div class="status-icon">
                            <span class="info"></span>
                        </div>
                        <div class="status-text">{{ validationMessage }}</div>
                    </div>
                </template>

                <div v-if="address.value && house" class="form-group">
                    <ValidatedInput
                        label=""
                        type="text"
                        placeholder="Введите квартиру/офис"
                        handle-validation-type="input"
                        :required="false"
                        :value="appart"
                        :external-valid="!!appart"
                        @handle-input="(val) => (appart = val)"
                        @handle-blur="setConfirmedApartment"
                    />
                </div>

                <div
                    v-if="
                        isFastDelivery &&
                        !processCheckingAddress &&
                        availableDeliveryStatus === DeliveryStatus.AVAILABLE
                    "
                    class="form-group"
                >
                    <div
                        v-if="realPrice && realDeliveryText"
                        class="delivery-info delivery-info--available"
                    >
                        <div class="delivery-info__row">
                            <span class="delivery-info__label"
                                >Стоимость доставки:</span
                            >
                            <span class="delivery-info__value"
                                >{{ realPrice }} ₽</span
                            >
                        </div>
                        <div class="delivery-info__row">
                            <span class="delivery-info__label"
                                >Плановое время доставки:</span
                            >
                            <span class="delivery-info__value">{{
                                detailedDeliveryTimes || realDeliveryText
                            }}</span>
                        </div>
                    </div>

                    <div v-if="nearestStore" class="store-info">
                        <div class="store-info__title">
                            Доставка будет осуществлена из магазина:
                        </div>
                        <div class="store-info__value">{{ nearestStore }}</div>
                    </div>
                </div>

                <div
                    v-if="isFastDelivery && externalCheckingStores"
                    class="status-area"
                >
                    <div class="status-block status-block--checking">
                        <div class="status-icon">
                            <span class="spinner"></span>
                        </div>
                        <div class="status-text">
                            Проверяем возможность доставки...
                        </div>
                    </div>
                </div>

                <div
                    v-if="address.value && house"
                    class="d-flex align-items-center"
                >
                    <button
                        type="button"
                        class="btn"
                        :class="[
                            isChecking ||
                            !canProceed ||
                            (!isFastDelivery && !coordinates.lat)
                                ? 'btn-secondary'
                                : 'btn-warning',
                        ]"
                        :disabled="
                            isChecking ||
                            !canProceed ||
                            (!isFastDelivery &&
                                (!coordinates.lat || !coordinates.lng))
                        "
                        @click="onContinueClick"
                    >
                        Продолжить
                    </button>
                    <div
                        v-if="isFastDelivery && !canProceed"
                        class="submit-suggest"
                    >
                        Укажите номер квартиры или офиса
                    </div>
                </div>
            </div>
        </div>
        <div class="map-address-clarification">
            <YandexMap
                :settings="settings"
                :options="options"
                :coordinates="mapCenter"
                :zoom="mapZoom"
                :controls="controls"
                :detailed-controls="detailedControls"
                @created="onMapCreated"
                @click="onMapClick"
            >
                <YandexMarker
                    v-if="markerCoords"
                    marker-id="address-clarification"
                    :coordinates="markerCoords"
                    :options="markerOptions"
                />
            </YandexMap>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { fastDeliveryGoal } from 'globalState/goals/';
    import { onMounted, ref, computed, watch, nextTick, shallowRef } from 'vue';
    import { YandexMap, YandexMarker } from 'vue-yandex-maps';
    import AutocompleteInput from 'vueCommon/components/autocomplete/Input.vue';
    import ValidatedInput from 'vueCommon/ui/ValidatedInput.vue';
    import { useMapConfig } from 'vueCommon/composables/useMapConfig.ts';
    import {
        useAddressAutocomplete,
        useFastDeliveryStatus,
        useAddressValidation,
        DeliveryStatus,
    } from '../composables/index.ts';

    const emits = defineEmits([
        'handle-set-data',
        'handle-close',
        'handle-clarification-apartment',
    ]);
    const props = defineProps({
        detailedDeliveryTimes: {
            type: String,
            default: '',
        },
        defaultAddress: {
            type: Object,
            default: () => ({}),
        },
        defaultAvailable: {
            type: Boolean,
            default: false,
        },
        defaultStore: {
            type: String,
            default: '',
        },
        realPrice: {
            type: [String, Number],
            default: '',
        },
        realDeliveryText: {
            type: String,
            default: '',
        },
        callbackCheckCoords: {
            type: Function,
            default: () => {},
        },
        externalCheckingStores: {
            type: Boolean,
            default: false,
        },
        mode: {
            type: String,
            default: 'fast',
            validator: (v: string) => ['fast', 'courier'].includes(v),
        },
    });

    const isFastDelivery = computed(() => props.mode === 'fast');
    const isChecking = computed(() =>
        isFastDelivery.value ? processCheckingAddress.value : address.process
    );

    const {
        address,
        coordinates,
        zip,
        city,
        settlement,
        street,
        house,
        korp,
        build,
        fetchSuggestions,
        setSuggestion,
        clearSuggestions,
        checkAddress,
        checkCoordinates,
    } = useAddressAutocomplete();

    const {
        availableDeliveryStatus,
        processCheckingAddress,
        nearestStore,
        warehouseCode,
        availableDeliveryText,
        availableDeliveryIcon,
        checkDeliveryAvailability,
        setDeliveryStatus,
        setNearestStore,
        resetDeliveryStatus,
    } = useFastDeliveryStatus();

    const appart = ref('');

    const { canProceed, validationMessage } = useAddressValidation(
        house,
        appart,
        isFastDelivery
    );

    const focusState = ref<boolean>(false);

    const {
        settings,
        controls,
        detailedControls,
        options,
        MAP_CONSTANTS,
        getDefaultCenter,
        coordinatesToArray,
    } = useMapConfig();

    const mapCenter = ref<[number, number]>(getDefaultCenter());
    const mapZoom = ref<number>(MAP_CONSTANTS.DEFAULT_ZOOM);
    const markerCoords = ref<[number, number] | null>(null);

    /** Instance ymaps.Map из vue-yandex-maps — для явного центрирования при открытии */
    const ymapInstance = shallowRef<unknown>(null);

    const markerOptions = {
        preset: 'islands#redDotIcon',
    };

    /**
     * Явно двигает вид карты к маркеру. panTo из watcher YandexMap при первом открытии
     * часто не успевает / не срабатывает сразу после init.
     */
    const syncMapToMarker = () => {
        const map = ymapInstance.value as {
            setCenter?: (..._args: unknown[]) => void;
        } | null;
        if (!map?.setCenter || !markerCoords.value) {
            return;
        }
        map.setCenter(markerCoords.value, MAP_CONSTANTS.SELECTED_ZOOM, {
            duration: 0,
        });
        mapCenter.value = [...markerCoords.value];
        mapZoom.value = MAP_CONSTANTS.SELECTED_ZOOM;
    };

    const onMapCreated = (map: unknown) => {
        ymapInstance.value = map;
        initPropsAfterMap();
        nextTick(() => {
            syncMapToMarker();
        });
    };

    /**
     * Клик по карте: новая точка адреса (как в legacy useYandexMap)
     * Для courier — только заполняет поля, не закрывает modal.
     */
    const onMapClick = (e: { get: (_key: string) => number[] }) => {
        const coords = e.get('coords');
        if (!coords?.length) {
            return;
        }

        if (isFastDelivery.value) fastDeliveryGoal.selectedPointByMap();
        focusState.value = false;
        address.suggestions = [];
        appart.value = '';

        coordinates.lat = String(coords[0]);
        coordinates.lng = String(coords[1]);

        markerCoords.value = [coords[0], coords[1]];
        mapCenter.value = [coords[0], coords[1]];
        mapZoom.value = MAP_CONSTANTS.SELECTED_ZOOM;

        checkCoordinates().then(() => {
            if (isFastDelivery.value) {
                getAvailableStores();
            }
            // courier: координаты и адрес уже заполнены, ждём "Продолжить"
        });
    };

    const initPropsAfterMap = () => {
        if (!props.defaultAddress.city) {
            const currentCityElement: any =
                document.querySelector('.current-geo');
            city.value = currentCityElement?.innerText;
        }

        const hasAddress = !!props.defaultAddress?.house;
        const needStore = isFastDelivery.value ? !!props.defaultStore : true;
        if (props.defaultAddress && hasAddress && needStore) {
            city.value = props.defaultAddress.city;
            settlement.value = props.defaultAddress.settlement;
            street.value = props.defaultAddress.street;
            house.value = props.defaultAddress.house;
            appart.value = props.defaultAddress.appart || '';
            korp.value = props.defaultAddress.korp || '';
            build.value = props.defaultAddress.build || '';
            address.value = `${city.value ? `г. ${city.value}` : ''} ${
                settlement.value ? `${settlement.value}` : ''
            } ${street.value ?? ''} ${house.value ? `д. ${house.value}` : ''}${
                korp.value ? ` к. ${korp.value}` : ''
            } ${build.value ? ` стр. ${build.value}` : ''}`;

            if (
                props.defaultAddress.coordinates &&
                props.defaultAddress.coordinates.lat &&
                props.defaultAddress.coordinates.lng
            ) {
                coordinates.lat = props.defaultAddress.coordinates.lat;
                coordinates.lng = props.defaultAddress.coordinates.lng;

                const arr = coordinatesToArray(coordinates);
                markerCoords.value = arr;
                mapCenter.value = arr;
                mapZoom.value = MAP_CONSTANTS.SELECTED_ZOOM;
            }

            if (isFastDelivery.value) {
                if (props.defaultAvailable) {
                    setDeliveryStatus(DeliveryStatus.AVAILABLE);
                } else {
                    setDeliveryStatus(DeliveryStatus.UNAVAILABLE);
                }
                if (props.defaultStore) {
                    setNearestStore(props.defaultStore);
                }
            }
        }
    };

    watch(
        () => props.defaultStore,
        (newVal) => {
            if (newVal) {
                setNearestStore(newVal);
            }
        }
    );

    /** Адрес из cookie приходит после mount / повторное открытие модалки */
    watch(
        () => [
            props.defaultAddress?.coordinates?.lat,
            props.defaultAddress?.coordinates?.lng,
        ],
        () => {
            if (!ymapInstance.value) {
                return;
            }
            initPropsAfterMap();
            nextTick(() => syncMapToMarker());
        }
    );

    onMounted(() => {
        if (isFastDelivery.value) fastDeliveryGoal.openModal();
    });

    watch(
        () => props.defaultAvailable,
        (newVal) => {
            if (newVal) {
                setDeliveryStatus(DeliveryStatus.AVAILABLE);
            } else {
                setDeliveryStatus(DeliveryStatus.UNAVAILABLE);
            }
        }
    );

    const formActive = computed(() => {
        return house.value || address.suggestions.length !== 0;
    });

    const processAutocomplete = computed(() => {
        return address.process || (!!address.value && focusState.value);
    });

    const selectSuggestionAddress = (suggestion: any) => {
        if (isFastDelivery.value) fastDeliveryGoal.selectedPointByAddress();
        setSuggestionAddress(suggestion);
    };

    const setSuggestionAddress = (suggestion: any) => {
        focusState.value = false;
        clearSuggestions();
        if (isFastDelivery.value) resetDeliveryStatus();

        setSuggestion(suggestion);
        appart.value = '';

        if (!suggestion.house) {
            fetchSuggestionsAddress(suggestion.mainValue);
            return;
        }

        checkAddress().then(() => {
            if (isFastDelivery.value) {
                getAvailableStores();
            }
            // courier: не вызываем setFinalData — закрытие только по "Продолжить"
        });
    };

    const getRegion = (): string =>
        city.value ||
        (props.defaultAddress as any)?.city ||
        (
            document.querySelector('.delivery-info__region') as HTMLElement
        )?.innerText?.trim() ||
        '';

    const fetchSuggestionsAddress = (query: string) => {
        const region = getRegion();

        if (isFastDelivery.value) {
            fetchSuggestions(query, ['Москва', 'Московская']);
        } else {
            fetchSuggestions(query, [], region ? [region] : []);
        }
    };

    const getAvailableStores = () => {
        checkDeliveryAvailability(coordinates, props.callbackCheckCoords).then(
            ({ store }) => {
                if (store) {
                    setFinalData();
                }
            }
        );
    };

    const setConfirmedApartment = () => {
        emits('handle-clarification-apartment', appart.value);
    };

    const setFinalData = () => {
        emits('handle-set-data', {
            address: {
                warehouse: warehouseCode.value,
                zip: zip.value,
                city: city.value,
                settlement: settlement.value,
                street: street.value,
                house: house.value,
                appart: appart.value,
                korp: korp.value,
                build: build.value,
                coordinates: {
                    lat: coordinates.lat,
                    lng: coordinates.lng,
                },
            },
            availableFastDelivery:
                availableDeliveryStatus.value === DeliveryStatus.AVAILABLE,
            nearestStore: nearestStore.value,
        });
    };

    const closeModal = () => {
        emits('handle-close');
    };

    const onContinueClick = () => {
        if (!canProceed.value || isChecking.value) return;
        if (!isFastDelivery.value && (!coordinates.lat || !coordinates.lng))
            return;
        if (!isFastDelivery.value) {
            setFinalData();
        }
        closeModal();
    };
</script>

<style scoped lang="less">
    .wrapper {
        display: flex;
        justify-content: space-between;
        width: 100%;
        height: 100%;
    }

    .form {
        width: 50%;
    }

    .form-wrapper {
        margin-top: 20px;
        padding: 0 10px;
    }

    .map-address-clarification {
        width: 50%;
        height: 100%;
        min-height: 320px;
    }

    .map-address-clarification :deep(.yandex-container) {
        width: 100%;
        height: 100%;
        min-height: 320px;
        border-radius: 8px;
        overflow: hidden;
    }

    .text-available {
        color: #008000;
        font-weight: bold;
        font-size: 16px;
        padding: 5px;
    }

    .placemarks-head__title {
        font-size: 24px;
        font-weight: 700;
    }

    .submit-suggest {
        align-items: center;
        color: #4e4e4e;
        font-size: 14px;
        margin-left: 20px;
    }

    // Стили для статусных блоков
    .status-area {
        margin: 15px 0;
    }

    .status-block {
        display: flex;
        align-items: center;
        padding: 12px 15px;
        border-radius: 6px;
        margin-bottom: 10px;

        &--checking {
            background-color: #fff9e6;
            border: 1px solid #ffe58f;
        }

        &--success {
            background-color: #f6ffed;
            border: 1px solid #b7eb8f;
        }

        &--warning {
            background-color: #fffbe6;
            border: 1px solid #ffe58f;
        }

        &--info {
            background-color: #e6f7ff;
            border: 1px solid #91d5ff;
        }
    }

    .status-icon {
        margin-right: 10px;
        display: flex;
        align-items: center;
        justify-content: center;

        .spinner {
            display: inline-block;
            width: 20px;
            height: 20px;
            border: 2px solid rgba(0, 0, 0, 0.1);
            border-top-color: #f5a623;
            border-radius: 50%;
            animation: spin 1s infinite linear;
        }

        .ok {
            display: inline-block;
            width: 20px;
            height: 20px;
            background-color: #52c41a;
            border-radius: 50%;
            position: relative;

            &:before {
                content: '';
                position: absolute;
                width: 10px;
                height: 5px;
                border-left: 2px solid white;
                border-bottom: 2px solid white;
                transform: rotate(-45deg);
                top: 6px;
                left: 5px;
            }
        }

        .info {
            display: inline-block;
            width: 20px;
            height: 20px;
            background-color: #1890ff;
            border-radius: 50%;
            position: relative;

            &:before {
                content: 'i';
                position: absolute;
                color: white;
                font-style: italic;
                font-weight: bold;
                top: 1px;
                left: 8px;
            }
        }
    }

    .status-text {
        font-size: 14px;
    }

    // Стили для информации о доставке
    .delivery-info {
        margin-top: 15px;
        padding: 12px 15px;
        background-color: #f5f5f5;
        border-radius: 6px;
        transition: all 0.3s ease;

        &--available {
            background-color: #f6ffed;
            border: 1px solid #b7eb8f;
        }

        &--unavailable {
            background-color: #fff7e6;
            border: 1px solid #ffe7ba;
            opacity: 0.9;
        }

        &__row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 8px;

            &:last-child {
                margin-bottom: 0;
            }
        }

        &__label {
            font-weight: 500;
            color: #666;
        }

        &__value {
            font-weight: 700;
            color: #222;
        }
    }

    // Стили для информации о магазине
    .store-info {
        margin-top: 15px;
        padding: 12px 15px;
        background-color: #f0f9ff;
        border-radius: 6px;
        transition: all 0.3s ease;

        &--unavailable {
            background-color: #f5f5f5;
            border: 1px solid #d9d9d9;
            opacity: 0.9;
        }

        &__title {
            font-weight: 500;
            color: #666;
            margin-bottom: 5px;
        }

        &__value {
            font-weight: 700;
            color: #222;
        }

        &__note {
            margin-top: 5px;
            font-size: 13px;
            color: #777;
            font-style: italic;
        }
    }

    @keyframes spin {
        0% {
            transform: rotate(0deg);
        }
        100% {
            transform: rotate(360deg);
        }
    }

    @media (max-width: 768px) {
        .wrapper {
            flex-direction: column;
        }

        .form {
            margin-top: 0;
        }

        .form,
        .map-address-clarification {
            width: 100%;
        }

        .form {
            position: absolute;
            bottom: 0;
            left: 0;
            z-index: 1;
            background: #fff;
            bottom: 60px;
        }

        .map-address-clarification {
            order: 0;
            max-height: calc(100% - 220px);
        }

        .placemarks-head {
            display: none;
        }
    }

    .modal-address-clarification .address-input {
        .autocomplete-suggestions {
            bottom: auto;
            top: 48px;
        }
    }
</style>
